import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';

const DateInputMask = ({
  value,
  onChange,
  disabledDate,
  placeholder,
  className,
  style,
  ...props
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [open, setOpen] = useState(false);

  const blurTimeoutRef = useRef(null);
  const isValidDateRef = useRef(false);

  const formatDateMask = (text) => {
    const numbers = text.replace(/\D/g, '');
    const limited = numbers.slice(0, 8);

    let formatted = '';

    for (let i = 0; i < limited.length; i++) {
      if (i === 2 || i === 4) {
        formatted += '-';
      }
      formatted += limited[i];
    }

    return formatted;
  };

  const isValidDate = (day, month, year) => {
    const date = new Date(year, month, day);

    return (
      date.getFullYear() === year &&
      date.getMonth() === month &&
      date.getDate() === day
    );
  };

  const convertToFullYear = (year) => {
      return 2000 + year;

  };

  const parseInputToDate = (numbers) => {
    if (numbers.length !== 6 && numbers.length !== 8) {
      return null;
    }

    const day = parseInt(numbers.slice(0, 2), 10);
    const month = parseInt(numbers.slice(2, 4), 10) - 1;

    let fullYear;

    if (numbers.length === 6) {
      const shortYear = parseInt(numbers.slice(4, 6), 10);
      fullYear = convertToFullYear(shortYear);
    } else {
      fullYear = parseInt(numbers.slice(4, 8), 10);
    }

    if (!isValidDate(day, month, fullYear)) {
      return null;
    }

    return dayjs(new Date(fullYear, month, day));
  };

  const handleInputChange = (e) => {
    const raw = e.target.value;
    const formatted = formatDateMask(raw);

    setInputValue(formatted);
    setIsTyping(true);

    const numbers = formatted.replace(/\D/g, '');

    if (numbers.length === 6 || numbers.length === 8) {
      const date = parseInputToDate(numbers);

      if (date && (!disabledDate || !disabledDate(date))) {
        isValidDateRef.current = true;
        onChange(date);
        return;
      }

      isValidDateRef.current = false;
      onChange(undefined);
    } else {
      isValidDateRef.current = false;
      onChange(undefined);
    }
  };

  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);

      if (!inputValue) {
        isValidDateRef.current = false;
        onChange(undefined);
        setInputValue('');
        return;
      }

      const numbers = inputValue.replace(/\D/g, '');

      if (numbers.length === 6 || numbers.length === 8) {
        const date = parseInputToDate(numbers);

        if (date && (!disabledDate || !disabledDate(date))) {
          isValidDateRef.current = true;
          setInputValue(date.format('DD-MM-YYYY'));
          onChange(date);
          return;
        }

        isValidDateRef.current = false;
        onChange(undefined);
        setInputValue('');
      } else if (numbers.length > 0 && numbers.length < 6) {
        onChange(undefined);
      } else {
        isValidDateRef.current = false;
        onChange(undefined);
        setInputValue('');
      }
    }, 200);
  };

  const handleFocus = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }

    setIsTyping(true);
  };

  const handleDatePickerChange = (date) => {
    if (date && date.isValid()) {
      isValidDateRef.current = true;
      onChange(date);
      setInputValue(date.format('DD-MM-YYYY'));
    } else {
      isValidDateRef.current = false;
      onChange(undefined);
      setInputValue('');
    }

    setOpen(false);
  };

  const handleDatePickerOpenChange = (isOpen) => {
    setOpen(isOpen);

    if (isOpen && blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    if (value && !isTyping) {
      if (dayjs.isDayjs(value) && value.isValid()) {
        setInputValue(value.format('DD-MM-YYYY'));
        isValidDateRef.current = true;
      } else {
        setInputValue('');
        isValidDateRef.current = false;
      }
    } else if (!value) {
      setInputValue('');
      isValidDateRef.current = false;
    }
  }, [value, isTyping]);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <input
        type="text"
        inputMode="numeric"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder || 'DD-MM-YYYY'}
        className={className || 'form-control control-input'}
        style={{
          MozAppearance: 'textfield',
          paddingRight: '35px',
          width: '100%',
          ...style
        }}
        {...props}
      />

      <DatePicker
        value={value}
        onChange={handleDatePickerChange}
        disabledDate={disabledDate}
        format="DD-MM-YYYY"
        placeholder=""
        open={open}
        onOpenChange={handleDatePickerOpenChange}
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          width: '35px',
          height: '100%',
          opacity: 1,
          border: 'none',
          background: 'transparent',
          padding: 0,
          minWidth: '35px'
        }}
      />
    </div>
  );
};

export default DateInputMask;